import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import * as d3 from 'd3';
import { find, findKey, result } from 'lodash';
import { Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { CellDisplayService } from 'Src/ng2/shared/services/list-services/cell-display.service';
import { RollupGroupingFooterService } from 'Src/ng2/shared/services/rollup-grouping-footer-service/rollup-grouping-footer-service';
import { unsubscribeComponent } from '../../../shared/helpers/unsubscribe-decorator/unsubscribe-decorators.helper';
import { IGroupData, IRowData } from '../../../shared/models/list-models';
import { DashboardCircleService } from '../../../shared/services/dashboard-circle-service/dashboard-circle-service';
import { IBar, IRow } from '../mid-level-dash.component';
import { IColumn } from './../../../shared/models/list-models';
import { VizDataService } from '../../../shared/services/viz-services/viz-data-service';
import { VizD3Service } from '../../../shared/services/viz-services/viz-d3-service';
import {
  IBarGraphData,
  IStackedBarGraphData,
  IxAxisRotation,
  VizDataType,
  TValidGraphType,
} from './../../../shared/models/viz-models';

@Component({
  selector: 'viz',
  template: require('./viz.component.html'),
  styles: [require('./viz.component.scss')],
})
@unsubscribeComponent
export class VizComponent implements OnInit, AfterViewInit, OnChanges {
  @ViewChild('viz', { static: true }) vizElement: ElementRef;
  @Input() rollupGrouping$: Observable<IGroupData>;
  @Input() focusData$: Observable<any>;
  @Input() focusChanged: boolean;
  @Input() hoveredTableRow: IRow;
  @Output() hoveredBar = new EventEmitter<IBar['group']>();
  rowData: IRowData[][];
  vizGroupingKey: string;
  graphType: TValidGraphType;
  vizDataType: VizDataType;

  svg: d3.Selection<SVGElement, any, any, any>;
  div;
  x;
  y;
  yNoData;
  xAxis;
  yAxis;
  yAxisNoData;
  width: number;
  height: number;
  margin = { top: 45, right: 20, bottom: 30, left: 40 };
  htmlElement: HTMLElement;
  initialized: boolean = false;
  xAxisRotation: IxAxisRotation;
  graphData: IBarGraphData;
  average: number;
  colorArray: Array<{ regular: string; bright: string }>;
  vizTitle: d3.Selection<SVGGElement, any, any, any>;
  mouseTarget: d3.Selection<SVGGElement, any, any, any>;
  xAxisGroup: d3.Selection<SVGGElement, any, any, any>;
  yAxisGroup: d3.Selection<SVGGElement, any, any, any>;
  barGroup: d3.Selection<SVGGElement, any, any, any>;
  avgerageGroup: d3.Selection<SVGGElement, any, any, any>;
  rollupGroupingSub: Subscription;
  focusDataSub: Subscription;
  midLevelColumns: IColumn[];
  totalRow: number;

  constructor(
    private dashboardCircleService: DashboardCircleService,
    private rollupGroupingFooterService: RollupGroupingFooterService,
    private vizDataService: VizDataService,
    private vizD3Service: VizD3Service,
  ) {}

  ngOnInit() {
    this.focusDataSub = this.focusData$
      .pipe(
        tap(focusData => {
          const { graphQlKeyVizGrouping, midLevelColumns, graphType } = focusData;
          // set new columns from updated focus, making them available to the fns reliant on groupData
          this.midLevelColumns = midLevelColumns;
          this.vizGroupingKey = graphQlKeyVizGrouping;
          this.graphType = graphType;
        }),
      )
      .subscribe();

    this.rollupGroupingSub = this.rollupGrouping$
      .pipe(
        tap(groupingData => {
          this.rowData = this.removeTotalRow(groupingData);
          // re/calculate average based on groupingData & columns before updating graph
          this.average = this.rowData.length > 0 ? this.computeAverage(this.rowData, this.midLevelColumns) : 0;
          if (this.initialized) this.updateGraphByType(this.rowData, this.graphType);
        }),
      )
      .subscribe();

    this.initialized = true;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.hoveredTableRow && this.hoveredTableRow) {
      let group = this.hoveredTableRow[0].data;
      this.highlightGraphByGroup(group, this.graphType);
    }
    if (changes.hoveredTableRow && changes.hoveredTableRow.currentValue === null) {
      this.showAllGraphGroups(this.graphType);
      this.div.style('opacity', 0);
    }
  }

  ngAfterViewInit() {
    this.buildSvgElement();
    this.buildGraphByType(this.rowData, this.graphType);
  }

  buildSvgElement(): void {
    this.htmlElement = this.vizElement.nativeElement;
    this.width = this.htmlElement.offsetWidth - 10;
    this.height = this.htmlElement.offsetHeight - 30;
    this.svg = d3
      .select(this.htmlElement)
      .append('svg')
      .attr('width', this.width)
      .attr('height', this.height);

    this.mouseTarget = this.svg.append('g').attr('transform', `translate(${this.margin.left},0)`);
    this.xAxisGroup = this.svg.append('g');
    this.yAxisGroup = this.svg.append('g');
    this.barGroup = this.svg.append('g');
    this.vizTitle = this.svg.append('g');
    this.avgerageGroup = this.svg.append('g').attr('transform', `translate(${this.margin.left},0)`);
    this.initialized = true;
  }

  // called in both create and update graph (cmac)
  preBarGraphFormat(data: IBarGraphData[]): void {
    this.xAxisRotation = this.vizDataService.getLabelRotation(data);
    this.colorArray = this.dashboardCircleService.getColors(data);
    this.createBarGraphScales(data);
    this.updateAxes(data);
  }

  removeTotalRow(groupData: IGroupData): IRowData[][] {
    const rowData = groupData.rowData.reduce((accum: IRowData[][], row: IRowData[]) => {
      let totalRow = find(row, { data: 'All' });
      if (totalRow) {
        this.totalRow = result(find(row, { columnKey: this.vizGroupingKey }), 'data');
      } else {
        accum.push(row);
      }
      return accum;
    }, []);
    return rowData;
  }

  computeAverage(rowData: IRowData[][], columns: IColumn[]): number {
    let average: number;
    const dataByColumnKey = this.rollupGroupingFooterService.getDataByColumnKey(rowData);
    const colKeyFooterMapping = this.rollupGroupingFooterService.createColKeyFooterMapping(columns);
    const primaryColKey = findKey(colKeyFooterMapping, (col: IColumn) => col.isFooterSetter);
    if (colKeyFooterMapping[primaryColKey].columnDataType === VizDataType.Count) {
      this.vizDataType = VizDataType.Count;
      const rowCount = dataByColumnKey[primaryColKey].length;
      const primaryColTotal = this.rollupGroupingFooterService.total(dataByColumnKey[primaryColKey]);
      average = rowCount ? Number((primaryColTotal / rowCount).toFixed(2)) : 0;
    } else {
      this.vizDataType = VizDataType.Percent;
      average = this.totalRow;
    }
    return average;
  }

  createBarGraphScales(data: IBarGraphData[]): void {
    const yAxisMax: number = d3.max(data, (d: IBarGraphData) => d.value);
    const yAxisDomainMax: number = yAxisMax === 0 ? 10 : yAxisMax;

    this.x = this.vizD3Service.createXScale(data, this.margin, this.width);
    this.y = this.vizD3Service.createYScale(yAxisDomainMax, this.margin, this.height);
    // used for no data display
    this.yNoData = this.vizD3Service.createYScaleNoData(this.margin, this.height);
  }

  createAxes() {
    this.xAxis = g => this.vizD3Service.createXAxis(this.height, this.margin, this.x, g);
    this.yAxis = g => this.vizD3Service.createYAxis(this.width, this.margin, this.y, g);
    this.yAxisNoData = g => this.vizD3Service.createYAxisNoData(this.width, this.margin, this.yNoData, g);
  }

  updateAxes(data: IBarGraphData[] | IStackedBarGraphData[]): void {
    this.xAxisGroup.call(this.xAxis);
    data.length > 0 ? this.yAxisGroup.call(this.yAxis) : this.yAxisGroup.call(this.yAxisNoData);

    this.svg
      .select('.x-axis')
      .selectAll('text')
      .attr('transform', `rotate(${this.xAxisRotation.rotation})`)
      .style('text-anchor', this.xAxisRotation.labelAnchor);

    // format labels to fit width of bars
    this.formatLabels(data, this.x.bandwidth());
  }

  addTitle(addTransition: boolean, data: IBarGraphData[] | IStackedBarGraphData[]): void {
    const title: string = data.length > 0 ? this.midLevelColumns[0].columnName : '';
    this.vizD3Service.createVizTitle(this.vizTitle, addTransition, title);
  }

  buildGraphByType(rowData: IRowData[][], graphType: TValidGraphType = 'Bar') {
    this.preGraphBuild();

    switch (graphType) {
      case 'Bar':
        const barGraphData = this.vizDataService.formatBarGraphData(rowData, this.vizGroupingKey);
        return this.buildBarGraph(barGraphData);
      case 'Stacked bar percent':
      case 'Stacked bar count':
        // ..TODO..
        break;
    }
  }

  preGraphBuild() {
    this.createAxes();
    this.mouseTarget = this.vizD3Service.buildMouseTarget(this.mouseTarget, this.width, this.margin.left, this.height);
  }

  // creates initial bar graph on load
  buildBarGraph(barGraphData: IBarGraphData[]) {
    const data = this.vizDataService.sortBarGraphData(barGraphData);
    this.preBarGraphFormat(data);

    const addTransition = true;
    this.addTitle(addTransition, data);

    // build tooltip div
    this.div = d3
      .select(this.htmlElement)
      .append('div')
      .attr('class', 'tooltip')
      .style('opacity', 0);

    const bars = this.barGroup
      .selectAll('path')
      .data(data)
      .enter()
      .append('path')
      .attr('class', 'bar')
      .attr('d', (d: IBarGraphData) => {
        return this.vizD3Service.getRectanglesPreTransition(this.x, this.y, d);
      })
      .attr('fill', (d: IBarGraphData, i: number) => this.colorArray[i].regular);

    bars
      .transition()
      .duration(1000)
      .attr('d', (d: IBarGraphData) => {
        return this.vizD3Service.getRectanglesPostTransition(this.x, this.y, d, this.height, this.margin);
      });

    const labels = this.xAxisGroup.selectAll('.tick').data(data);

    // on mouseover
    bars.on('mouseover', (d: IBarGraphData, i: number) => {
      this.shareHoveredBar(d.group);
      this.displayBar(d.group);
    });

    labels.on('mouseover', (d, i) => {
      this.shareHoveredBar(d.group);
      this.displayBar(d.group);
    });

    // on mouseout
    bars.on('mouseout', (d: IBarGraphData, i: number) => {
      this.shareHoveredBar(null);
    });

    labels.on('mouseout', (d: IBarGraphData, i: number) => {
      this.shareHoveredBar(null);
    });

    d3.selectAll('.mouseTarget').on('mouseout', () => {
      this.showAllBars();
      this.div.style('opacity', 0);
    });

    // only build average line if displaying data
    data.length > 0 ? this.buildAverageLine() : this.removeAverageLine();

    return this.svg.node();
  }

  updateGraphByType(rowData: IRowData[][], graphType: TValidGraphType = 'Bar') {
    switch (graphType) {
      case 'Bar':
        const barGraphData: IBarGraphData[] = this.vizDataService.formatBarGraphData(rowData, this.vizGroupingKey);
        return this.updateBarGraph(barGraphData);
      case 'Stacked bar percent':
      case 'Stacked bar count':
        // ..TODO..
        break;
    }
  }

  updateBarGraph(barGraphData: IBarGraphData[]): void {
    const data = this.vizDataService.sortBarGraphData(barGraphData);
    this.removeExistingBars();
    this.preBarGraphFormat(data);

    this.addTitle(this.focusChanged, data);
    this.div.style('opacity', 0);

    const bars: any = this.barGroup.selectAll('path').data(data);

    bars
      .enter()
      .append('path')
      .attr('class', 'bar')
      .merge(bars)
      .attr('d', (d: IBarGraphData) => {
        return this.vizD3Service.getRectanglesPreTransition(this.x, this.y, d);
      })
      .attr('fill', (d: IBarGraphData, i: number) => this.colorArray[i].regular);

    const updatedBars = this.barGroup.selectAll('path').data(data);

    updatedBars
      .transition()
      .duration(1000)
      .attr('d', d => {
        return this.vizD3Service.getRectanglesPostTransition(this.x, this.y, d, this.height, this.margin);
      });

    bars.exit().remove();

    const labels = this.xAxisGroup.selectAll('.tick').data(data);

    updatedBars.on('mouseover', (d: IBarGraphData, i: number) => {
      this.shareHoveredBar(d.group);
      this.displayBar(d.group);
    });

    labels.on('mouseover', (d, i) => {
      this.shareHoveredBar(d.group);
      this.displayBar(d.group);
    });

    updatedBars.on('mouseout', (d: IBarGraphData, i: number) => {
      this.shareHoveredBar(null);
      this.div.style('opacity', 0);
    });

    labels.on('mouseout', (d: IBarGraphData, i: number) => {
      this.shareHoveredBar(null);
    });

    d3.selectAll('.mouseTarget').on('mouseout', () => {
      this.showAllBars();
      this.div.style('opacity', 0);
    });

    // only build average line if displaying data
    data.length > 0 ? this.buildAverageLine() : this.removeAverageLine();
  }

  getLabelRotation(data: IBarGraphData[]): IxAxisRotation {
    const groupCount = data.length;
    const maxLength = 8;
    const labelAnchor = groupCount > maxLength ? 'start' : 'middle';
    const rotation = groupCount > maxLength ? 15 : 0;
    const axisRotation = { labelAnchor, rotation };
    return axisRotation;
  }

  // if data length greater than 24, replace text labels with circles.
  // if data is greater than 8, increase width since labels are on a slant.
  formatLabels(data: IBarGraphData[] | IStackedBarGraphData[], width: number): void {
    this.removeCircleLabels();
    this.removeNoDataLabel();
    if (data.length > 24) {
      this.addCircleLabels(data as IBarGraphData[], width);
      this.removeTextLabels();
    } else if (data.length > 8) {
      width = width - 5;
      this.formatTextLabels(width);
    } else if (data.length < 1) {
      this.addNoDataLabel();
    } else {
      this.formatTextLabels(width);
    }
  }

  // remove existing circles
  removeCircleLabels(): void {
    this.svg
      .selectAll('circle')
      .remove()
      .exit();
  }

  // remove text labels
  removeTextLabels(): void {
    const nodes = d3.selectAll('.x-axis text').nodes();
    nodes.forEach((node: any) => {
      let tspan = d3.select(node);
      tspan.text('');
    });
  }

  // removes bar and path groups for bar and stacked bar graphs (cmac)
  removeExistingBars(): void {
    this.barGroup.selectAll('.bar').remove();
    this.barGroup.selectAll('path').remove();
    this.barGroup.selectAll('g').remove();
  }

  // if text wider than bar, shorten and add ellipsis
  formatTextLabels(width: number): void {
    const nodes = d3.selectAll('.x-axis text').nodes();
    nodes.forEach((node: any) => {
      let text = node.textContent.toString();
      let nodeLength = node.getComputedTextLength();
      while (nodeLength > width && width > 30) {
        let tspan = d3.select(node);
        text = text.slice(0, -1);
        tspan.text(`${text}...`);
        nodeLength = tspan.node().getComputedTextLength();
      }
    });
  }

  addNoDataLabel(): void {
    d3.selectAll('.x-axis')
      .append('text')
      .attr('class', 'no-data-label')
      .attr('x', this.width / 2)
      .attr('y', 25)
      .text('No data found');
  }

  removeNoDataLabel(): void {
    d3.selectAll('.no-data-label').remove();
  }

  // adds circles to x-axis
  addCircleLabels(data: IBarGraphData[], width: number): void {
    this.xAxisGroup
      .selectAll('circle')
      .data(data)
      .enter()
      .append('circle')
      .attr('cx', d => {
        return this.x(d.group) + width / 2;
      })
      .attr('cy', 10)
      .attr('r', width / 2)
      .attr('fill', (d: IBarGraphData, i: number) => {
        return this.colorArray[i].regular;
      });
  }

  removeAverageLine(): void {
    this.avgerageGroup
      .selectAll('.avg-text')
      .remove()
      .exit();

    this.avgerageGroup
      .selectAll('.avg-line')
      .remove()
      .exit();
  }

  buildAverageLine(): void {
    this.removeAverageLine();

    const text = this.avgerageGroup
      .append('text')
      .attr('class', 'avg-text')
      .attr('x', this.width - this.margin.left - this.margin.right)
      .attr('y', this.y(this.average) + 3);

    const averageDisplayVal = CellDisplayService.formatDisplayValue({
      colVal: this.average,
      colDataType: this.vizDataType,
    });

    text
      .append('tspan')
      .text(averageDisplayVal)
      .style('opacity', 0)
      .transition()
      .duration(2000)
      .style('opacity', 1);

    const textWidth = text.node().getComputedTextLength() + 8;
    const avgLabelHeight = d3.select(this.y(this.average)).node();
    const avgLabelPosition = avgLabelHeight < 22 ? this.y(this.average) + 15 : this.y(this.average) - 15;

    text
      .append('tspan')
      .attr('class', 'avg-label')
      .attr('x', this.width - this.margin.left - this.margin.right)
      .attr('y', avgLabelPosition)
      .text('Average')
      .style('opacity', 0)
      .transition()
      .duration(2000)
      .style('opacity', 0.6);

    this.avgerageGroup
      .append('line')
      .attr('class', 'avg-line')
      .attr('x1', 1)
      .attr('y1', this.y(this.average))
      .attr('x2', this.width - this.margin.left - this.margin.right - textWidth)
      .attr('y2', this.y(this.average))
      .style('opacity', 0)
      .transition()
      .duration(2000)
      .style('opacity', 1);
  }

  displayBar(group: string): void {
    d3.selectAll('.bar').attr('fill', (d: IBarGraphData, i: number) => {
      if (d.group === group) {
        this.displayTooltip(d.group, d.value, i);
        return this.colorArray[i].bright;
      } else {
        return this.dashboardCircleService.shadeRGBColor(this.colorArray[i].regular, 0.7);
      }
    });
  }

  showAllBars(): void {
    d3.selectAll('.bar').attr('fill', (d: IBarGraphData, i: number) => {
      return this.colorArray[i].regular;
    });
  }

  highlightGraphByGroup(group: string, graphType: TValidGraphType = 'Bar'): void {
    switch (graphType) {
      case 'Bar':
        this.displayBar(group);
        break;
      case 'Stacked bar percent':
      case 'Stacked bar count':
        // ..TODO..
        break;
    }
  }

  showAllGraphGroups(graphType: TValidGraphType = 'Bar'): void {
    switch (graphType) {
      case 'Bar':
        this.showAllBars();
        break;
      case 'Stacked bar percent':
      case 'Stacked bar count':
        // ..TODO..
        break;
    }
  }

  displayTooltip(group: string, value: number, i: number): void {
    const nodes = d3.selectAll('.bar').nodes();
    const tooltipDisplayVal = CellDisplayService.formatDisplayValue({ colVal: value, colDataType: this.vizDataType });
    nodes.forEach((node: any) => {
      const clientRect = node.getBoundingClientRect();
      const clientText = node.__data__.group;
      if (clientText === group) {
        this.div
          .html(
            `<span>
              <div class='circle' style='background:${this.colorArray[i].bright}'></div>
              <div class='value'>${tooltipDisplayVal}</div>
              <div class='group'>${group}</div>
             </span>`,
          )
          .style('left', clientRect.left + clientRect.width / 2 + 'px')
          .style('top', clientRect.top - 10 + 'px')
          .style('opacity', 0.9);
      }
    });
  }

  shareHoveredBar(barGroup: IBar['group']): void {
    this.hoveredBar.emit(barGroup);
  }
}
